// School court schedule — the high-school beach season on paper, as the 2026
// OCPS Court Schedule spreadsheet kept it: a week grid of courts by half hour,
// a season table of court-hours per school, and the meet days.
//
// Sessions live in schoolSessions/{id}. Each one has a twin at
// reservations/{same id} carrying only date, times and courts, titled
// "School use": that twin is what blocks the courts in member booking (client
// and server already check reservations) and what everyone else sees on the
// calendar. Practice and meet details stay in schoolSessions.
//
// Pure helpers here; AdminSchoolSchedule.js does the reads and writes.

export const COURTS = ['court-1', 'court-2', 'court-3', 'court-4', 'court-5', 'court-6'];
export const courtLabel = (c) => `Court ${String(c).replace('court-', '')}`;
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const KINDS = {
  practice: { label: 'Practice' },
  meet: { label: 'Meet' },
  dropin: { label: 'Drop-in' },
};

// A dual takes 3 courts for 2 hours or 6 courts for 1 hour; a 3-way is three
// duals. `hours` sets the end time when the format is picked; a tournament has
// no standard length, so it leaves the times alone.
export const MEET_FORMATS = {
  dual: { label: 'Dual, 3 courts × 2 h', courts: 3, hours: 2 },
  dual6: { label: 'Dual, 6 courts × 1 h', courts: 6, hours: 1 },
  tri: { label: '3-way, 6 courts × 3 h', courts: 6, hours: 3 },
  tri3: { label: '3-way, 3 courts × 6 h', courts: 3, hours: 6 },
  tournament: { label: 'Tournament', courts: 6 },
};

// Colors handed to schools in the order they're added. Picked to read on white
// with dark text, like the spreadsheet's cell fills.
export const SCHOOL_PALETTE = ['#F9E79F', '#AED6F1', '#A9DFBF', '#F5B7B1', '#D7BDE2', '#FAD7A0',
  '#A3E4D7', '#D5DBDB', '#F8C471', '#85C1E9'];

export const toMins = (t) => {
  const [h, m] = String(t || '0:0').split(':').map(Number);
  return h * 60 + (m || 0);
};
export const fromMins = (n) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;

export const clock = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}${h >= 12 ? ' PM' : ' AM'}`;
};

// Dates as YYYY-MM-DD strings, walked in UTC so no timezone or DST shift can
// skip or repeat a day.
const parse = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const fmt = (dt) => dt.toISOString().slice(0, 10);
export const addDays = (s, n) => {
  const dt = parse(s);
  dt.setUTCDate(dt.getUTCDate() + n);
  return fmt(dt);
};
export const dayOfWeek = (s) => parse(s).getUTCDay();

/** The Monday of the week a date falls in. */
export function weekStart(s) {
  const dow = dayOfWeek(s);
  return addDays(s, dow === 0 ? -6 : 1 - dow);
}

/** Every Monday from the week of `start` through the week of `end`. */
export function seasonWeeks(start, end) {
  if (!start || !end || end < start) return [];
  const out = [];
  for (let w = weekStart(start); w <= end; w = addDays(w, 7)) out.push(w);
  return out;
}

/**
 * The dates a weekly practice falls on: each of `days` (0 = Sunday) from
 * `from` through `to`, minus any date in `skip` (spring break, a holiday).
 */
export function seriesDates({ from, to, days = [], skip = [] }) {
  if (!from || !to || to < from) return [];
  const want = new Set(days.map(Number));
  const skipped = new Set(skip);
  const out = [];
  for (let d = from; d <= to; d = addDays(d, 1)) {
    if (want.has(dayOfWeek(d)) && !skipped.has(d)) out.push(d);
  }
  return out;
}

export const hoursOf = (s) => Math.max(0, (toMins(s.endTime) - toMins(s.startTime)) / 60);
export const courtHours = (s) => hoursOf(s) * (Array.isArray(s.courts) ? s.courts.length : 0);

/** Drop-in price: courts × hours × the court-hour rate, in cents. */
export function dropInCents({ courts = [], startTime, endTime }, rateDollars) {
  return Math.round(courts.length * hoursOf({ startTime, endTime }) * (Number(rateDollars) || 0) * 100);
}

/** Court-hours per school code over a list of sessions, and the total. */
export function hoursBySchool(sessions) {
  const by = {};
  let total = 0;
  for (const s of sessions) {
    const h = courtHours(s);
    by[s.schoolCode] = (by[s.schoolCode] || 0) + h;
    total += h;
  }
  return { by, total };
}

/**
 * Sessions that share a court with `candidate` at an overlapping time on the
 * same date. Touching ends (4:00–5:00 then 5:00–6:00) don't overlap.
 */
export function conflicts(sessions, candidate) {
  const a0 = toMins(candidate.startTime);
  const a1 = toMins(candidate.endTime);
  const courts = new Set(candidate.courts || []);
  return sessions.filter(s => s.id !== candidate.id
    && s.date === candidate.date
    && toMins(s.startTime) < a1 && a0 < toMins(s.endTime)
    && (s.courts || []).some(c => courts.has(c)));
}

/**
 * Half-hour rows for a week grid: the school window, widened to take in any
 * session that starts earlier or runs later (meets often go past 6:00).
 */
export function gridSlots(sessions, windowStart = '14:00', windowEnd = '18:30') {
  let lo = toMins(windowStart);
  let hi = toMins(windowEnd);
  for (const s of sessions) {
    lo = Math.min(lo, toMins(s.startTime));
    hi = Math.max(hi, toMins(s.endTime));
  }
  lo = Math.floor(lo / 30) * 30;
  const out = [];
  for (let m = lo; m < hi; m += 30) out.push(fromMins(m));
  return out;
}

/** The session holding `court` during the half hour starting at `slot`, if any. */
export function sessionAt(sessions, date, court, slot) {
  const m = toMins(slot);
  return sessions.find(s => s.date === date && (s.courts || []).includes(court)
    && toMins(s.startTime) <= m && m < toMins(s.endTime));
}

/** The calendar twin: only what blocks the courts, nothing about who. */
export function reservationFor(session) {
  return {
    title: 'School use',
    type: 'school',
    date: session.date,
    startTime: session.startTime,
    endTime: session.endTime,
    courts: session.courts,
    schoolSessionId: session.id,
  };
}

// ── The FHSAA season calendar ────────────────────────────────────────────────
// Everything hangs off week 1, the first week of regular-season games (Feb 23
// in 2026). Weeks count from it, so week 0 is the week before and week -2 the
// one before that. Each date can be overridden in Settings when a year differs:
//   first allowable practice   week -2, Monday
//   Pre-Season Classic         week 0, Monday–Saturday
//   spring break               week 2, Monday–Friday
//   last week for games        week 8
//   districts                  week 9
//   regionals                  three separate dates: week 10 Monday and
//                              Thursday, week 11 Tuesday (Apr 27, Apr 30 and
//                              May 5 in 2026)
//   State Finals               week 11, Friday and Saturday

/** The Monday of week n, counting week 1 as the week of `week1`. */
export const weekMonday = (week1, n) => addDays(weekStart(week1), (n - 1) * 7);

/** The calendar a week-1 date implies. */
export function calendarFromWeek1(week1) {
  if (!week1) return null;
  const wk = (n) => weekMonday(week1, n);
  return {
    week1: wk(1),
    firstPractice: wk(-2),
    classicStart: wk(0),
    classicEnd: addDays(wk(0), 5),
    springBreakStart: wk(2),
    springBreakEnd: addDays(wk(2), 4),
    lastGamesStart: wk(8),
    lastGamesEnd: addDays(wk(8), 4),
    districtsStart: wk(9),
    districtsEnd: addDays(wk(9), 4),
    regionals1: wk(10),
    regionals2: addDays(wk(10), 3),
    regionals3: addDays(wk(11), 1),
    statesStart: addDays(wk(11), 4),
    statesEnd: addDays(wk(11), 5),
  };
}

export const CALENDAR_FIELDS = [
  { key: 'firstPractice', label: 'First allowable practice (week −2)' },
  { key: 'classicStart', end: 'classicEnd', label: 'Pre-Season Classic (week 0)' },
  { key: 'week1', label: 'Week 1 (first games)' },
  { key: 'springBreakStart', end: 'springBreakEnd', label: 'Spring break (week 2)' },
  { key: 'lastGamesStart', end: 'lastGamesEnd', label: 'Last week for games (week 8)' },
  { key: 'districtsStart', end: 'districtsEnd', label: 'Districts (week 9)' },
  { key: 'regionals1', label: 'Regionals, first date (week 10)' },
  { key: 'regionals2', label: 'Regionals, second date (week 10)' },
  { key: 'regionals3', label: 'Regionals, third date (week 11)' },
  { key: 'statesStart', end: 'statesEnd', label: 'State Finals (week 11, Fri–Sat)' },
];

const REGIONALS = ['regionals1', 'regionals2', 'regionals3'];
const weeksBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / (7 * 86400000));
const inWeek = (monday, d) => !!d && weekStart(d) === monday;
const overlapsWeek = (monday, a, b) => !!a && !!b && weekStart(a) <= monday && monday <= b;

/**
 * What a week is called, as the spreadsheet's rows were: "Preseason wk 2",
 * "Practice wk 1", "Pre-Season Classic", "Duals wk 3", "Spring Break",
 * "Districts", "Regionals", "State Finals". `monday` is the week's Monday.
 * Returns '' outside the calendar or when there is no calendar.
 */
export function weekLabel(monday, cal, preseasonStart) {
  if (!cal?.week1) return '';
  const w1 = weekStart(cal.week1);
  const regionals = REGIONALS.some(k => inWeek(monday, cal[k]));
  const states = overlapsWeek(monday, cal.statesStart, cal.statesEnd);
  if (regionals || states) return [regionals && 'Regionals', states && 'State Finals'].filter(Boolean).join(' · ');
  if (overlapsWeek(monday, cal.districtsStart, cal.districtsEnd)) return 'Districts';
  if (monday >= w1) {
    const lastGames = cal.lastGamesStart ? weekStart(cal.lastGamesStart) : weekMonday(w1, 8);
    if (monday > lastGames) return '';
    const n = weeksBetween(w1, monday) + 1;
    return overlapsWeek(monday, cal.springBreakStart, cal.springBreakEnd) ? `Duals wk ${n} · Spring Break` : `Duals wk ${n}`;
  }
  if (overlapsWeek(monday, cal.classicStart, cal.classicEnd)) return 'Pre-Season Classic';
  if (cal.firstPractice && monday >= weekStart(cal.firstPractice)) {
    return `Practice wk ${weeksBetween(weekStart(cal.firstPractice), monday) + 1}`;
  }
  if (preseasonStart && monday >= weekStart(preseasonStart)) {
    return `Preseason wk ${weeksBetween(weekStart(preseasonStart), monday) + 1}`;
  }
  return '';
}

/** The calendar milestone a single date falls on, for a day header. */
export function dayMilestone(date, cal) {
  if (!cal?.week1) return '';
  if (date === cal.firstPractice) return 'First practice';
  if (date === weekStart(cal.week1)) return 'Week 1';
  const inRange = (a, b) => a && b && date >= a && date <= b;
  if (REGIONALS.some(k => cal[k] === date)) return 'Regionals';
  if (inRange(cal.statesStart, cal.statesEnd)) return 'State Finals';
  if (inRange(cal.classicStart, cal.classicEnd)) return 'Classic';
  if (inRange(cal.springBreakStart, cal.springBreakEnd)) return 'Spring break';
  if (inRange(cal.districtsStart, cal.districtsEnd)) return 'Districts';
  return '';
}
